import { useEffect, useState } from "react";

import { moveSvgProblems } from "../domain/moveArt";
import "./MoveFigure.css";

/*
 * One fetch per drawing per launch, shared by every slot that shows it — the sheet and the player
 * can open the same move a dozen times in a session. A miss is remembered too, so a move with no
 * drawing asks once. The service worker precaches the files, so even the first fetch is local.
 */
const seen = new Map<string, Promise<string | null>>();

function load(id: string): Promise<string | null> {
  let p = seen.get(id);
  if (!p) {
    p = fetch(`${import.meta.env.BASE_URL}moves/${encodeURIComponent(id)}.svg`)
      .then((r) => (r.ok ? r.text() : ""))
      // A dev server answers a missing path with the app's HTML, so only a body that passes the
      // drawing check counts — and the check also keeps anything but one currentColor path out
      // of the page this markup is inlined into.
      .then((t) => (moveSvgProblems(t).length ? null : t))
      .catch(() => null);
    seen.set(id, p);
  }
  return p;
}

/**
 * A move's approved line drawing from `public/moves/<id>.svg` (ADR-058), or nothing at all when
 * the move has none — no frame, no placeholder, no error. Inlined rather than an `<img>` so its
 * fill is `currentColor` and follows the theme: the slot paints `--ink` on `--bg` (the sheet),
 * and `paper` puts it on `--paper` (the Simple player's card). Hidden from a screen reader,
 * because the move's name is the heading beside it.
 */
export function MoveFigure({ id, paper, className }: { id: string; paper?: boolean; className?: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setSvg(null);
    void load(id).then((t) => {
      if (live) setSvg(t);
    });
    return () => {
      live = false;
    };
  }, [id]);
  if (!svg) return null;
  const cls = ["k-figure", paper ? "k-figure--paper" : "", className ?? ""].filter(Boolean).join(" ");
  return <div className={cls} aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;
}
